import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "", state: "",
    referral_code: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Capture referral code from ?ref=CODE query param or localStorage (persists across pages)
  useEffect(() => {
    const fromUrl = params.get("ref");
    const stored = window.localStorage.getItem("tlr_ref");
    if (fromUrl) {
      window.localStorage.setItem("tlr_ref", fromUrl.toUpperCase());
      setForm((f) => ({ ...f, referral_code: fromUrl.toUpperCase() }));
    } else if (stored) {
      setForm((f) => ({ ...f, referral_code: stored }));
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await register({
        ...form,
        referral_code: form.referral_code ? form.referral_code.toUpperCase() : null,
      });
      if (res?.bonus_credits) {
        toast.success(`Welcome! You got ${res.bonus_credits} free contact reveals from your referral 🎁`);
      } else {
        toast.success("Account created — welcome!");
      }
      window.localStorage.removeItem("tlr_ref");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const refApplied = !!form.referral_code;

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-2xl border border-brand-subtle p-8">
        <p className="label-eyebrow text-gray-500 mb-2">Get started</p>
        <h1 className="font-display text-4xl brand-forest mb-8">Create your account</h1>

        {refApplied && (
          <div data-testid="referral-banner" className="mb-6 p-4 rounded-xl bg-[#FBF6EC] border border-[#C9A24A]/50 flex items-start gap-3">
            <Gift size={18} className="text-[#7A1E2A] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#1B0F14]">Referral code applied: <span className="text-[#7A1E2A]">{form.referral_code}</span></p>
              <p className="text-xs text-gray-600 mt-0.5">You'll get <strong>5 free contact reveals</strong> when you finish signing up.</p>
            </div>
          </div>
        )}

        <GoogleAuthButton label="Sign up with Google" />

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-[#EFE3D0]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">or with email</span>
          <div className="flex-1 h-px bg-[#EFE3D0]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Full name</label>
            <Input data-testid="signup-name" value={form.name} onChange={set("name")} required />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Email</label>
            <Input data-testid="signup-email" type="email" value={form.email} onChange={set("email")} required />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Phone (shown when you list a property)</label>
            <Input data-testid="signup-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">City</label>
              <Input data-testid="signup-city" value={form.city} onChange={set("city")} placeholder="e.g. Bengaluru" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">State</label>
              <Input data-testid="signup-state" value={form.state} onChange={set("state")} placeholder="e.g. Karnataka" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Password</label>
            <Input data-testid="signup-password" type="password" value={form.password} onChange={set("password")} minLength={6} required />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block flex items-center gap-1">
              Referral code <span className="text-xs text-gray-500 font-normal">(optional, get 5 free reveals)</span>
            </label>
            <Input
              data-testid="signup-referral"
              value={form.referral_code}
              onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
              placeholder="e.g. RAVI-XVAT"
              maxLength={12}
            />
          </div>
          <Button data-testid="signup-submit" type="submit" disabled={busy} className="w-full tlr-gold-btn rounded-full h-12">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-gray-500 mt-6 text-center">
          Already have an account? <Link to="/login" className="brand-forest font-semibold hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
